import React, {
  useState
} from 'react';

import {
  Spin,
  Tabs
} from 'antd';

import {
  Tab
} from 'rc-tabs/lib/interface';

import {
  useTranslation
} from 'react-i18next';

import CoordinateInfo, {
  CoordinateInfoProps,
  CoordinateInfoState
} from '@terrestris/react-geo/dist/CoordinateInfo/CoordinateInfo';
import {
  useMap
} from '@terrestris/react-geo/dist/Hook/useMap';
import {
  WmsLayer,
  isWmsLayer
} from '@terrestris/react-geo/dist/Util/typeUtils';

import FeatureInfoPropertyGrid from '../FeatureInfoPropertyGrid';

type LayerIndex = {
  layerName: string;
  index: number;
};

export type FeatureInfoProps = Omit<CoordinateInfoProps, 'map'>;

export const FeatureInfo: React.FC<Partial<FeatureInfoProps>> = ({
  ...passThroughProps
}): JSX.Element => {

  const [activeTabKey, setActiveTabKey] = useState<string>();

  const map = useMap();

  const {
    t
  } = useTranslation();

  if (!map) {
    return <></>;
  }

  const getUnqualifiedLayerName = (layerName: string) => {
    return layerName.split(':').length > 1 ?
      layerName.split(':')[1] :
      layerName.split(':')[0];
  };

  const findMapLayerIndex = (layerName: string) => {
    const allLayers = map.getAllLayers();

    return allLayers.findIndex(l => {
      if (isWmsLayer(l)) {
        const source = (l as WmsLayer).getSource();
        const unqualifiedMapLayerName = getUnqualifiedLayerName(source?.getParams().LAYERS);
        const unqualifiedLayerName = getUnqualifiedLayerName(layerName);

        return unqualifiedLayerName === unqualifiedMapLayerName;
      }
      return false;
    });
  };

  const onTabClick = (key: string) => {
    setActiveTabKey(key);
  };

  const resultRenderer = (result: CoordinateInfoState) => {
    const features = result.features;
    const loading = result.loading;

    if (Object.keys(features).length === 0) {
      return (
        <span className='usage-hint'>
          {t('FeatureInfo.usageHint')}
        </span>
      );
    }

    const items: Array<Tab & { index: number }> = [];

    items.sort((a, b) => b.index - a.index);

    Object.keys(features).forEach(layerName => {
      const allLayers = map.getAllLayers();

      const mapLayerIndex = findMapLayerIndex(layerName);
      const mapLayer = allLayers[mapLayerIndex];

      items.push({
        label: mapLayer?.get('name') ?? layerName,
        index: mapLayerIndex,
        key: layerName,
        children: (
          <FeatureInfoPropertyGrid
            features={features[layerName]}
            layerName={layerName}
            loading={loading}
          />
        )
      });
    });

    return (
      <Spin
        spinning={loading}
      >
        <Tabs
          destroyInactiveTabPane={true}
          items={items}
          activeKey={activeTabKey}
          defaultActiveKey={items[0].key}
          onTabClick={onTabClick}
        />
      </Spin>
    );
  };

  const onSuccess = (coordinateInfoState: CoordinateInfoState) => {
    const features = coordinateInfoState.features;

    const layers: LayerIndex[] = Object.keys(features)
      .map(layerName => ({
        layerName: layerName,
        index: findMapLayerIndex(layerName)
      }))
      .sort((a, b) => b.index - a.index);

    if (layers.length > 0) {
      setActiveTabKey(layers[0].layerName);
    }
  };

  const queryLayers = map.getAllLayers()
    .filter(layer => layer.isVisible())
    .filter(layer => isWmsLayer(layer));

  return (
    <CoordinateInfo
      map={map}
      queryLayers={queryLayers as WmsLayer[]}
      resultRenderer={resultRenderer}
      featureCount={10}
      drillDown={true}
      onSuccess={onSuccess}
      {...passThroughProps}
    />
  );
};

export default FeatureInfo;
